/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import {
  HiOutlineChatAlt2,
  HiOutlineEye,
  HiOutlineRefresh,
  HiOutlineTrash,
} from "react-icons/hi";
import { FaRegCheckSquare, FaRegSquare } from "react-icons/fa";

import ListPage from "../../layout/ListPage";
import {
  Button,
  Card,
  ConfirmDialog,
  IconButton,
} from "../../ui";
import type { Action, Column } from "../../ui";
import Service from "../../api/service";
import { _PAGE_SIZE_ } from "../../lib/other/tools";
// @ts-ignore — existing JSX companion.
import Preview from "./Preview";
// @ts-ignore — existing JSX message component.
import { MyMessage } from "../components/messageUtils";

type ChatRow = {
  open_ai_chat_id: number | string;
  createdAt?: string;
  OpenAiChatTabItems?: Array<{ question?: string }>;
  [key: string]: unknown;
};

const Overview: React.FC = () => {
  const [chats, setChats] = useState<ChatRow[] | null>(null);
  const [count, setCount] = useState<number | null>(null);
  const [prompt, setPrompt] = useState<string>("");
  const [promptId, setPromptId] = useState<number | string | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(_PAGE_SIZE_);
  const [query, setQuery] = useState("");

  const [selectedIds, setSelectedIds] = useState<(number | string)[]>([]);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  const [previewChat, setPreviewChat] = useState<ChatRow | null>(null);

  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!chats) getChats(page, pageSize);
    if (!promptId) getPrompt();
  }, []);

  const getChats = async (p = 1, size = _PAGE_SIZE_) => {
    const { data } = await Service.get(`/api/v1/ai-chat/?page=${p}&pageSize=${size}`);
    if (data) {
      setCount(data.count);
      setChats(data.data);
    }
  };

  const getPrompt = async () => {
    const { data } = await Service.get(`/api/v1/ai-context-one`);
    if (data) {
      setPrompt(data?.context ?? "");
      setPromptId(data?.open_ai_context_id);
    }
  };

  const savePrompt = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!promptId) return;
    setLoading(true);
    const { status } = await Service.put(`/api/v1/ai-context/${promptId}`, {
      context: prompt,
    });
    const ok = status === 200 || status === 201;
    setLoading(false);
    setError(!ok);
    setSuccess(ok);
    setMessage(ok ? "Prompt updated." : "Failed to update prompt.");
  };

  const deleteOne = async (id: number | string) => {
    await Service.delete(`/api/v1/ai-chat/${id}`);
  };

  const deleteMany = async () => {
    setBulkLoading(true);
    for (const id of selectedIds) await deleteOne(id);
    setBulkLoading(false);
    setSelectedIds([]);
    setShowBulkDelete(false);
    await getChats(page, pageSize);
  };

  const rows = useMemo<ChatRow[] | null>(() => {
    if (!chats) return null;
    const q = query.trim().toLowerCase();
    if (!q) return chats;
    return chats.filter((c) =>
      String(c.OpenAiChatTabItems?.[0]?.question ?? "")
        .toLowerCase()
        .includes(q),
    );
  }, [chats, query]);

  const visibleIds = useMemo(
    () => (rows ?? []).map((r) => r.open_ai_chat_id),
    [rows],
  );

  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => visibleIds.includes(id)));
  }, [visibleIds]);

  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const toggleAll = () => {
    setSelectedIds((prev) =>
      allVisibleSelected
        ? prev.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...prev, ...visibleIds])),
    );
  };

  const toggleOne = (id: number | string) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const columns: Column<ChatRow>[] = [
    {
      header: "Select",
      className: "w-16",
      cell: (row) => (
        <IconButton
          aria-label={selectedIds.includes(row.open_ai_chat_id) ? "Unselect" : "Select"}
          variant="ghost"
          size="sm"
          onClick={() => toggleOne(row.open_ai_chat_id)}
        >
          {selectedIds.includes(row.open_ai_chat_id) ? (
            <FaRegCheckSquare className="h-4 w-4 text-brand" />
          ) : (
            <FaRegSquare className="h-4 w-4 text-gray-400" />
          )}
        </IconButton>
      ),
    },
    {
      header: "Question",
      cell: (row) => (
        <p className="line-clamp-2 text-gray-900">
          {row.OpenAiChatTabItems?.[0]?.question ?? "—"}
        </p>
      ),
    },
    {
      header: "When",
      cell: (row) =>
        row.createdAt ? (
          <span className="text-[11.5px] text-gray-500">
            {new Date(row.createdAt).toLocaleString()}
          </span>
        ) : (
          <span className="text-gray-300">—</span>
        ),
    },
  ];

  const rowActions = (row: ChatRow): Action[] => [
    {
      label: "Preview",
      icon: <HiOutlineEye className="h-3.5 w-3.5" />,
      onClick: () => setPreviewChat(row),
    },
    {
      label: "Delete",
      variant: "destructive",
      icon: <HiOutlineTrash className="h-3.5 w-3.5" />,
      onClick: async () => {
        await deleteOne(row.open_ai_chat_id);
        await getChats(page, pageSize);
      },
      confirm: {
        title: "Delete this chat?",
        description: "The conversation will be permanently removed.",
        confirmLabel: "Delete",
      },
    },
  ];

  const bulkToolbar = (
    <Card padding="sm" className="flex flex-wrap items-center gap-2">
      <Button
        variant="secondary"
        size="sm"
        onClick={toggleAll}
        disabled={visibleIds.length === 0}
      >
        {allVisibleSelected ? (
          <FaRegCheckSquare className="h-3.5 w-3.5 text-brand" />
        ) : (
          <FaRegSquare className="h-3.5 w-3.5" />
        )}
        {allVisibleSelected ? "Unselect visible" : "Select visible"}
      </Button>

      <Button
        variant={selectedIds.length > 0 ? "destructive" : "secondary"}
        size="sm"
        onClick={() => selectedIds.length > 0 && setShowBulkDelete(true)}
        disabled={selectedIds.length === 0 || bulkLoading}
      >
        <HiOutlineTrash className="h-3.5 w-3.5" />
        {selectedIds.length > 0
          ? `Delete ${selectedIds.length} ${selectedIds.length === 1 ? "chat" : "chats"}`
          : "Delete selected"}
      </Button>

      <Button variant="ghost" size="sm" onClick={() => getChats(page, pageSize)}>
        <HiOutlineRefresh className="h-3.5 w-3.5" />
        Refresh
      </Button>
    </Card>
  );

  const promptSection = (
    <>
      <MyMessage
        error={error}
        message={message}
        success={success}
        onExpired={() => {
          setError(false);
          setSuccess(false);
        }}
        onClick={() => {
          setError(false);
          setSuccess(false);
          setMessage("");
        }}
      />

      <form onSubmit={savePrompt}>
        <Card padding="md" className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">
                AI Prompt Context
              </h2>
              <p className="text-xs text-gray-500">
                This context guides chat generation and responses.
              </p>
            </div>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? "Saving…" : "Save changes"}
            </Button>
          </div>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={6}
            className="w-full rounded-xl border border-gray-200 bg-white p-3 text-sm text-gray-900 shadow-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
          />
          <div className="text-right text-xs text-gray-500">
            {prompt.length.toLocaleString()} characters
          </div>
        </Card>
      </form>
    </>
  );

  return (
    <>
      <ListPage<ChatRow>
        title="Chat support"
        subtitle="Assistant conversations, filter and review."
        icon={<HiOutlineChatAlt2 className="h-5 w-5" />}
        topSlot={promptSection}
        toolbar={bulkToolbar}
        rows={rows}
        columns={columns}
        rowKey={(r) => r.open_ai_chat_id}
        rowActions={rowActions}
        search={{
          value: query,
          onChange: setQuery,
          placeholder: "Search by question…",
        }}
        pagination={{
          page,
          pageSize,
          total: count,
          onChange: ({ page: p, pageSize: ps }) => {
            setPage(p);
            setPageSize(ps);
            getChats(p, ps);
          },
        }}
        empty="No chats yet."
      />

      <ConfirmDialog
        open={showBulkDelete}
        destructive
        title={`Delete ${selectedIds.length} ${selectedIds.length === 1 ? "chat" : "chats"}?`}
        description="This cannot be undone."
        confirmLabel={bulkLoading ? "Deleting…" : "Delete"}
        onCancel={() => setShowBulkDelete(false)}
        onConfirm={deleteMany}
      />

      {previewChat ? (
        <Preview
          OpenAiChatTabItems={previewChat.OpenAiChatTabItems}
          setShowPreview={(v: boolean) => !v && setPreviewChat(null)}
          loading={false}
          onDelete={async () => {
            await deleteOne(previewChat.open_ai_chat_id);
            setPreviewChat(null);
            await getChats(page, pageSize);
          }}
        />
      ) : null}
    </>
  );
};

export default Overview;
